/**
 * Dossier file names as the server builds them (crates/dossier/src/file_names.rs; owner
 * 2026-10-01, like HostBill): `<domain>-<document id>.<ext>` — the domain's A-label with dots as
 * dashes, the catalogue's integer number (`dossier.document_types.code`), `-2`, `-3` for a second
 * file of a type. The server sends the names (`DomainDocument.file_name`); the demo backend uses
 * this to show the same thing.
 *
 * Pure functions (no React): `npm test` covers them.
 */

/** The catalogue number of a binding purpose (seed of migration 0280, HostBill's document ids). */
export const DOCUMENT_CODE: Readonly<Record<string, number>> = {
  REGISTRATION_FORM: 14, DOMAIN_DECLARATION: 14, SIGNED_DECLARATION: 14, REGISTRATION_DECLARATION: 14, TRANSFEREE_DECLARATION: 14, NEW_HOLDER_DECLARATION: 14,
  IDENTITY_CARD_FRONT: 15, IDENTITY_CARD_BACK: 16, REGISTRAR_CHANGE_FORM: 17, CHANGE_INFORMATION_REQUEST: 18, SENSITIVE_KEYWORD_COMMITMENT: 19,
  RETURN_REQUEST: 20, GOV_VN_COMMITMENT: 21, SIGNED_ORGANIZATION_INFO: 27, BUSINESS_REGISTRATION: 29, PASSPORT: 40, SERVICE_CONTRACT: 41,
};

/** "Hồ sơ khác": a file whose purpose has no type. */
export const OTHER_CODE = 42;

/** One name: `<ascii domain>-<code>[-<n>].<ext>`. */
export function dossierFileName(asciiDomain: string, code: number, ext: string, ordinal = 1): string {
  const stem = asciiDomain.trim().toLowerCase().replace(/\.$/, '').replace(/\./g, '-');
  return `${stem}-${code}${ordinal > 1 ? `-${ordinal}` : ''}.${ext.replace(/^\./, '').toLowerCase()}`;
}

export function extensionOf(mime: string): string {
  return mime === 'application/pdf' ? 'pdf' : mime === 'image/png' ? 'png' : mime === 'image/jpeg' ? 'jpg' : (mime.split('/').pop() ?? 'bin');
}

/** The names of a dossier's files, in the order they were bound. */
export function dossierFileNames(asciiDomain: string, files: readonly { document_version_id: string; purpose: string; mime: string }[]): Map<string, string> {
  const used = new Map<number, number>();
  const names = new Map<string, string>();
  for (const f of files) {
    if (names.has(f.document_version_id)) continue;
    const code = DOCUMENT_CODE[f.purpose] ?? OTHER_CODE;
    const n = (used.get(code) ?? 0) + 1;
    used.set(code, n);
    names.set(f.document_version_id, dossierFileName(asciiDomain, code, extensionOf(f.mime), n));
  }
  return names;
}
